/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { ProgramRevenue } from 'hooks/analytics';
import { AnalyticsColors, thSx, tdSx } from '../../components/analyticsUi';
import CollapsibleSection from '../../components/CollapsibleSection';
import { formatCurrency, formatPercentage } from 'utils/formatters';
import { REVENUE_BY_PROGRAM } from '../../constants/metricDescriptions';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

const CHART_COLORS = ['#2563EB', '#7C3AED', '#0891B2', '#D97706', '#059669', '#DC2626'];

interface RevenueMixProps {
    programs: ProgramRevenue[];
    totalRevenue: number;
}

const RevenueMix = ({ programs, totalRevenue }: RevenueMixProps) => {
    const chartData = useMemo(
        () => ({
            labels: programs.map((p) => p.programName),
            datasets: [
                {
                    label: 'Revenue',
                    data: programs.map((p) => p.totalRevenue),
                    backgroundColor: CHART_COLORS.slice(0, programs.length),
                    borderRadius: 6,
                    barThickness: 40,
                    hoverBackgroundColor: CHART_COLORS.slice(0, programs.length).map(
                        (c) => c + 'DD'
                    ),
                },
            ],
        }),
        [programs]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#1F2937',
                    titleFont: { size: 12, weight: '600' as const },
                    bodyFont: { size: 13 },
                    padding: 10,
                    cornerRadius: 8,
                    callbacks: {
                        label: (ctx: any) => ` Revenue: ${formatCurrency(ctx.raw)}`,
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: (val: any) => `$${(val / 1000).toFixed(0)}K`,
                        font: { size: 11 },
                        color: '#9CA3AF',
                    },
                    grid: { color: '#F3F4F6' },
                    border: { display: false },
                },
                x: {
                    grid: { display: false },
                    ticks: {
                        maxRotation: 0,
                        font: { size: 11 },
                        color: '#6B7280',
                    },
                    border: { display: false },
                },
            },
        }),
        []
    );

    return (
        <CollapsibleSection
            id="revenue-by-program"
            title="Revenue by Program"
            tooltip={REVENUE_BY_PROGRAM}
        >
            <Box sx={{ height: 280, mb: '1.75rem' }}>
                <Bar data={chartData} options={chartOptions as any} />
            </Box>

            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead" sx={{ position: 'sticky', top: 0 }}>
                    <tr>
                        <Box component="th" sx={thSx()}>
                            Program
                        </Box>
                        <Box component="th" sx={thSx({ align: 'right' })}>
                            Cases
                        </Box>
                        <Box component="th" sx={thSx({ align: 'right' })}>
                            Avg Fee
                        </Box>
                        <Box
                            component="th"
                            sx={{ ...thSx({ align: 'left' }), pl: '1.5rem' } as any}
                        >
                            % of Total
                        </Box>
                        <Box component="th" sx={thSx({ align: 'right' })}>
                            Revenue
                        </Box>
                    </tr>
                </Box>
                <tbody>
                    {programs.map((p, i) => (
                        <Box
                            component="tr"
                            key={p.programName}
                            sx={{
                                background: i % 2 === 1 ? AnalyticsColors.surfaceBg : 'transparent',
                                transition: 'background 0.1s ease',
                                '&:hover': { background: '#F3F0FF' },
                            }}
                        >
                            <Box component="td" sx={tdSx()}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '3px',
                                            background: CHART_COLORS[i] || CHART_COLORS[0],
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span style={{ fontWeight: 600 }}>{p.programName}</span>
                                </Box>
                            </Box>
                            <Box component="td" sx={tdSx({ align: 'right' })}>
                                {p.caseCount}
                            </Box>
                            <Box component="td" sx={tdSx({ align: 'right' })}>
                                {formatCurrency(p.avgFeePerCase)}
                            </Box>
                            <Box
                                component="td"
                                sx={{ ...tdSx({ align: 'left' }), pl: '1.5rem' } as any}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: 6,
                                            background: '#F3F4F6',
                                            borderRadius: '3px',
                                            maxWidth: 60,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${p.percentOfTotal}%`,
                                                background: CHART_COLORS[i] || CHART_COLORS[0],
                                                borderRadius: '3px',
                                                transition: 'width 0.4s ease',
                                            }}
                                        />
                                    </Box>
                                    <span
                                        style={{
                                            fontSize: '13px',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {formatPercentage(p.percentOfTotal)}
                                    </span>
                                </Box>
                            </Box>
                            <Box component="td" sx={tdSx({ align: 'right', bold: true })}>
                                {formatCurrency(p.totalRevenue)}
                            </Box>
                        </Box>
                    ))}
                    <Box
                        component="tr"
                        sx={{
                            '& td': {
                                fontWeight: '700 !important',
                                fontSize: 14,
                                color: AnalyticsColors.textPrimary,
                                borderTop: `2px solid ${AnalyticsColors.textPrimary}`,
                                borderBottom: 'none',
                                pt: '0.85rem',
                            },
                        }}
                    >
                        <Box component="td" sx={tdSx()}>
                            Total
                        </Box>
                        <Box component="td" sx={tdSx({ align: 'right' })}>
                            {programs.reduce((s, p) => s + p.caseCount, 0)}
                        </Box>
                        <Box component="td" sx={tdSx({ align: 'right' })}>
                            {totalRevenue
                                ? formatCurrency(
                                      totalRevenue / programs.reduce((s, p) => s + p.caseCount, 0)
                                  )
                                : 0}
                        </Box>
                        <Box
                            component="td"
                            sx={{ ...tdSx({ align: 'left' }), pl: '1.5rem' } as any}
                        >
                            100.0%
                        </Box>
                        <Box component="td" sx={tdSx({ align: 'right' })}>
                            {formatCurrency(totalRevenue)}
                        </Box>
                    </Box>
                </tbody>
            </Box>
        </CollapsibleSection>
    );
};

export default RevenueMix;
